/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiSelectors, uiTypes, useSetupDialog } from '@opengeoweb/store';
import { SoundingsProfileDataMenu } from './SoundingsProfileDataMenu';
import { soundingsActions } from '../../store/reducer';
import { getProfileDataPreferences } from '../../store/selectors';
import type { SoundingsRootState } from '../../store/types';
import { SoundingsActionOrigin } from '../../store/types';
import type { SoundingsProfileDataPreferences } from '../../utils/profileDataPreferences';

export const SoundingsProfileDataDialogConnect = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { isDialogOpen, onCloseDialog, dialogOrder, setDialogOrder } =
    useSetupDialog(uiTypes.DialogTypes.SoundingsProfileData);

  const viewId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.SoundingsProfileData),
  );

  const preferences = useSelector((state: SoundingsRootState) =>
    getProfileDataPreferences(state, viewId),
  );

  const handlePreferencesChange = React.useCallback(
    (next: SoundingsProfileDataPreferences): void => {
      dispatch(
        soundingsActions.updateProfileDataPreferences({
          profileDataPreferences: next,
          viewId,
          origin: SoundingsActionOrigin.soundingsComponent,
        }),
      );
    },
    [dispatch, viewId],
  );

  return (
    <SoundingsProfileDataMenu
      handleClose={onCloseDialog}
      isOpen={isDialogOpen}
      onMouseDown={setDialogOrder}
      onPreferencesChange={handlePreferencesChange}
      order={dialogOrder}
      preferences={preferences}
    />
  );
};
